/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useEffect, useRef, useMemo, useState } from "react";
import { SelectChangeEvent, Button as MuiButton, SxProps, Tooltip } from "@mui/material";
import { RadixSelectControl } from "../../ui/radix-select";
import { RadixFilterMultiSelect } from "../../ui/radix-multi-select";
import { ThunkDispatch } from "redux-thunk";
import { IFilterSelectBox } from "./types";
import FilterListIcon from "@mui/icons-material/FilterList";
import { Stack } from "@mui/system";
import SearchBox from "../SearchBox/SearchBox";
import { FilterAltOffOutlined } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import { Button } from "../../ui/button";
import { FilterX } from "lucide-react";
import { resetKeyword, resetKeywordForScope } from "../../../store/search/reducer";
import { capitalizeWords } from "../../../utils/helpers";
import { useSelector } from "react-redux";
import { eventFilterEventFromTimeSelector, eventFilterEventToTimeFilterSelector } from "../../../store/filters/events/EventFilterSelectors";
import DateRangeCalendarPicker from "../../../components/Shared/Calendar/DateRangeCalendarPicker";
import dayjs, { Dayjs } from "dayjs";
import { setSelectedFromEventTime, setSelectedToEventTime, setSelectedMaintenanceMode } from "../../../store/filters/events/EventFilterReducer";
import { setSelectedNavListItems } from "../../../store/helpers/helperReducers";
import { useRegionDisplay } from "../../../pages/Administrations/AccessControls/Regions/hooks";
import { deviceRegionFilterIdsSelector, deviceSiteGroupSelector, deviceMaintenanceModeSelector } from "../../../store/filters/Nodes/selectors";
import { setRegionFilterIds, setSelectedDeviceSiteGroup, setMaintenanceMode, setSelectedDeviceStatus, setSelectedDeviceKpiStatus, setSelectedDeviceConfigStatus } from "../../../store/filters/Nodes/reducer";
import { searchKeywordByScopeSelector, searchKeywordSelector } from "../../../store/search/selector";
import { selectedNavListItemSelector } from "../../../store/helpers/helperSelectors";
import { setSelectedGeoRegion } from "../../../store/api/admin/reducers";
import { initialGeoRegion } from "../../../components/helpers";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { AppState } from "../../../store";
import { OverflowFilterBar, OverflowFilterItem } from "../../ui/overflow-filter-bar";

interface IGeneralFilterProps {
  children?: React.ReactNode;
  dispatch: ThunkDispatch<any, any, any>;
  filters: IFilterSelectBox[];
  formControlSx: any;
  keyName?: string;
  resetFilters: () => void;
  isEnableApllFilter?: boolean;
  searchTooltip?: string;
  showSearch?: boolean;
  searchScope?: string;
  searchWidth?: number | string;
  timeFilterConfig?: {
    fromSelector: (state: AppState) => number | undefined;
    toSelector: (state: AppState) => number | undefined;
    setFrom: (value: number) => any;
    setTo: (value: number) => any;
  };
  resetButtonClassName?: string;
  onReset?: () => void;
  externalSearchActive?: boolean;
}

export const GeneralFilter: React.FC<IGeneralFilterProps> = ({
  children,
  dispatch,
  filters,
  formControlSx,
  resetFilters,
  isEnableApllFilter = false,
  searchTooltip = "",
  showSearch = true,
  searchScope,
  searchWidth,
  timeFilterConfig,
  resetButtonClassName,
  onReset,
  externalSearchActive = false,
}) => {
  const formatFilterOptionLabel = (label: string, preserveCase?: boolean) => {
    return preserveCase ? label : capitalizeWords(label);
  };

  const selectdefaultSxprops: SxProps = {
    color: "var(--link)",
    //backgroundColor: "var(--secondary)",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    "& .MuiSelect-icon": {
      color: "var(--link)",
    },
  };


  const { geoRegions } = useRegionDisplay();
  const searchKeyword = useSelector(searchKeywordSelector);
  const scopedKeyword = useSelector(searchKeywordByScopeSelector(searchScope || ""));
  const selectedNavItem = useSelector(selectedNavListItemSelector);
  const selectedDeviceSiteGroup = useSelector(deviceSiteGroupSelector);
  const regionFilterIds = useSelector(deviceRegionFilterIdsSelector);
  const maintenanceMode = useSelector(deviceMaintenanceModeSelector);
  const timeSelectors = timeFilterConfig || {
    fromSelector: eventFilterEventFromTimeSelector,
    toSelector: eventFilterEventToTimeFilterSelector,
    setFrom: setSelectedFromEventTime,
    setTo: setSelectedToEventTime,
  };
  const eventFromTimeFilter = useSelector(timeSelectors.fromSelector);
  const eventToTimeFilter = useSelector(timeSelectors.toSelector);
  const [isCustomDatePopoverOpen, setIsCustomDatePopoverOpen] = useState(false);
  const timeFilterAnchorRef = useRef<HTMLDivElement | null>(null);

  const openCustomDatePopover = () => {
    setIsCustomDatePopoverOpen(true);
  };

  const deviceLabelFilter = filters.find(filter => filter.id === "deviceLabel");
  const regionFilter = filters.find(filter => filter.id === "regionFilter");

  const handleFromTimeOnChange = (dateTime: Dayjs | null | undefined) => dispatch(timeSelectors.setFrom(dateTime?.valueOf() || 0));
  const handleToTimeOnChange = (dateTime: Dayjs | null | undefined) => dispatch(timeSelectors.setTo(dateTime?.valueOf() || 0));

  const handleResetFilterOnClick = () => {
    dispatch(resetFilters());
    if (searchScope) {
      dispatch(resetKeywordForScope({ scope: searchScope }));
    } else {
      dispatch(resetKeyword());
    }
    if (deviceLabelFilter)
      dispatch(deviceLabelFilter?.setValue([]));

    // Reset region filter
    const regionFilter = filters.find(filter => filter.id === "regionFilter");
    if (regionFilter) {
      dispatch(regionFilter.setValue([]));
    }

    // Clearing filters should always return the left nav quick filter to All devices.
    dispatch(setSelectedNavListItems("devices"));
    dispatch(setSelectedDeviceSiteGroup(""));

    if (onReset) onReset();
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLSelectElement> | SelectChangeEvent) => {
    const filter = filters.find(filter => filter.id === e.target.name);

    if (filter) {
      if (filter.id === "geoRegion") {
        const isExist = geoRegions.find(o => o.objectKey === e.target.value);
        const name = isExist ? isExist.name : "";
        dispatch(filter.setValue({ name: name, objectKey: e.target.value || "" }));
      }
      else
        dispatch(filter.setValue(e.target.value));

      const selectedValue = { 'type': filter.id, 'value': e.target.value };
      if (selectedValue.type === "time") {
        if (String(selectedValue.value) === "Custom") {
          openCustomDatePopover();
        } else {
          setIsCustomDatePopoverOpen(false);
        }
      }
      const hasRegionFilter = Array.isArray(regionFilterIds) && regionFilterIds.length > 0;
      const hasSiteGroup = !!selectedDeviceSiteGroup;
      const canApplyQuickFilter = !hasRegionFilter && !hasSiteGroup;

      switch (true) {
        case selectedValue.type === 'status' && selectedValue.value === '19':
          if (canApplyQuickFilter) {
            dispatch(setSelectedNavListItems("disconnected"));
          }
          break;
        case selectedValue.type === 'status' && selectedValue.value === '':
          if (selectedNavItem === "disconnected") {
            dispatch(setSelectedNavListItems("devices"));
            dispatch(setRegionFilterIds([]));
            dispatch(setSelectedDeviceSiteGroup(""));
          }
          break;
        case selectedValue.type === 'configstatus' && selectedValue.value === 'Offline':
          if (canApplyQuickFilter) {
            dispatch(setSelectedNavListItems("offline"));
          }
          break;
        case selectedValue.type === 'configstatus' && selectedValue.value === '':
          if (selectedNavItem === "offline") {
            dispatch(setSelectedNavListItems("devices"));
            dispatch(setRegionFilterIds([]));
            dispatch(setSelectedDeviceSiteGroup(""));
          }
          break;
        case selectedValue.type === 'kpistatus' && selectedValue.value === 'false':
          if (canApplyQuickFilter) {
            dispatch(setSelectedNavListItems("pmCollected"));
          }
          break;
        case selectedValue.type === 'kpistatus' && selectedValue.value === '':
          if (selectedNavItem === "pmCollected") {
            dispatch(setSelectedNavListItems("devices"));
            dispatch(setRegionFilterIds([]));
            dispatch(setSelectedDeviceSiteGroup(""));
          }
          break;
        default:
          break;
      }
    };
  }

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const multiSelectorSxProps: SxProps = {
    width: 200,

    "& .MuiOutlinedInput-root": {
      borderRadius: 1,
      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.4)",
      cursor: "pointer",

      "&:hover fieldset": {
        borderColor: "primary.main",
      },
    },

    // Checkbox
    "& .MuiCheckbox-root.Mui-checked": {
      color: "primary.main",
    },

    "& .MuiChip-root": {
      borderRadius: "2px",
      height: 24,
    },

    "& .MuiChip-label": {
      px: 1.5,
      fontSize: "0.75rem"
    },
  }

  const multiSelectorSearchSxProps: SxProps = {
    color: "primary",
    "& .MuiAutocomplete-popupIndicator": {
      color: "primary.main",
    },
  }

  const isValueActive = (value: unknown) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return value !== undefined && value !== null && value !== "";
  };

  const hasActiveFilters = filters.some((filter) => isValueActive(filter.value));
  const hasSearchKeyword = (!!searchKeyword && searchKeyword.trim().length > 0) || (!!scopedKeyword && scopedKeyword.trim().length > 0);
  const hasDateFilters = !!eventFromTimeFilter || !!eventToTimeFilter;
  const hasMaintenanceMode = !!maintenanceMode;
  const showReset = hasActiveFilters || hasSearchKeyword || hasDateFilters || hasMaintenanceMode || externalSearchActive;
  const resetButtonSizeClass = resetButtonClassName || "h-8 min-h-8 max-h-8 w-8 min-w-8 max-w-8";

  const hasInitialized = useRef(false);

  useEffect(() => {
    const hasRegionFilter = Array.isArray(regionFilterIds) && regionFilterIds.length > 0;
    const hasSiteGroup = !!selectedDeviceSiteGroup;
    const statusFilter = filters.find((item) => item.id === "status");
    const configFilter = filters.find((item) => item.id === "configstatus");
    const kpiFilter = filters.find((item) => item.id === "kpistatus");

    const isDisconnected = String(statusFilter?.value ?? "") === "19";
    const isOffline = configFilter?.value === "Offline";
    const isKpiError = kpiFilter?.value === "false";
    const quickFilterKey =
      isDisconnected ? "disconnected" :
        isOffline ? "offline" :
          isKpiError ? "pmCollected" :
            "";

    // On first run, restore filter state to match the nav item persisted in localStorage.
    // This prevents the useEffect from overriding the stored nav selection with "devices".
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      if (selectedNavItem && selectedNavItem !== "devices" && !showReset && !hasRegionFilter && !hasSiteGroup) {
        switch (selectedNavItem) {
          case "disconnected":
            dispatch(setSelectedDeviceStatus("19"));
            break;
          case "offline":
            dispatch(setSelectedDeviceConfigStatus("Offline"));
            break;
          case "pmCollected":
            dispatch(setSelectedDeviceKpiStatus("false"));
            break;
          case "maintenancemode":
            dispatch(setMaintenanceMode("true"));
            dispatch(setSelectedMaintenanceMode("1"));
            break;
          default:
            if (typeof selectedNavItem === 'string' && selectedNavItem.startsWith("region:")) {
              // Region key — restore the region filter
              dispatch(setRegionFilterIds([selectedNavItem.substring(7)]));
            } else if (typeof selectedNavItem === 'string') {
              // Site group key — restore it as the active site group filter
              dispatch(setSelectedDeviceSiteGroup(selectedNavItem));
            }
            break;
        }
        return;
      }
    }

    if (!hasRegionFilter && !hasSiteGroup && quickFilterKey) {
      dispatch(setSelectedNavListItems(quickFilterKey));
      return;
    }

    // If the nav panel already shows a recognized quick filter (e.g. "offline",
    // "disconnected"), don't reset it to "devices".  This happens when the
    // Alarms/Events/Conditions tabs render their own filter bar which lacks
    // the "status"/"configstatus"/"kpistatus" filter entries, so quickFilterKey
    // above resolves to "" even though the quick filter is still active.
    const recognizedQuickFilters = ["disconnected", "offline", "pmCollected", "maintenancemode"];
    if (selectedNavItem && recognizedQuickFilters.includes(selectedNavItem)) {
      return;
    }

    if (!showReset && !hasRegionFilter && !hasSiteGroup) {
      dispatch(setSelectedNavListItems("devices"));
    }
  }, [dispatch, showReset, regionFilterIds, selectedDeviceSiteGroup, selectedNavItem, filters]);

  const parseWidth = (w?: string): number => {
    if (!w) return 120;
    const n = parseInt(w, 10);
    return isNaN(n) ? 120 : n;
  };

  const estimateTimeFilterWidth = (label: string): number => {
    const WIDTH = 54;
    const MIN_WIDTH = 170;
    const MAX_WIDTH = 320;
    let textWidth: number;
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.font = "14px Inter, system-ui, sans-serif";
        textWidth = ctx.measureText(label).width;
      } else {
        textWidth = label.length * 6.5;
      }
    } catch {
      textWidth = label.length * 6.5;
    }
    return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.ceil(textWidth + WIDTH)));
  };

  const overflowItems: OverflowFilterItem[] = useMemo(() => {
    return filters
      .map((filter: IFilterSelectBox): OverflowFilterItem => {
        if (filter.id === "deviceLabel") {
          const labelOptions = (Array.isArray(filter.values) ? filter.values : []).map((v: any) =>
            typeof v === "string" ? { value: v, label: v } : { value: v.value, label: v.text ?? v.label ?? v.value }
          );
          const selectedLabels = Array.isArray(filter.value) ? filter.value : [];
          return {
            key: filter.id,
            width: parseWidth(filter.width),
            content: (
              <RadixFilterMultiSelect
                options={labelOptions}
                value={selectedLabels}
                onValueChange={(selected) => {
                  filter.setValue && dispatch(filter.setValue(selected));
                }}
                placeholder={capitalizeWords(filter.label)}
                width={filter?.width || "110px"}
              />
            ),
          };
        }

        if (filter.id === "time") {
          const from = eventFromTimeFilter ? dayjs(eventFromTimeFilter) : null;
          const to = eventToTimeFilter ? dayjs(eventToTimeFilter) : null;
          const customLabel =
            from && to
              ? `${from.format("MMM D, YYYY")} - ${to.format("MMM D, YYYY")}`
              : "Custom";
          const isCustomSelection = String(filter.value ?? "") === "Custom";
          const nonCustomFixedWidth = Math.max(parseWidth(filter.width), 170);
          const customDynamicWidth = estimateTimeFilterWidth(customLabel);
          const timeOptions = (Array.isArray(filter.values) ? filter.values : []).map((item: any) => {
            if (typeof item === "object") {
              return {
                value: item?.value,
                label: formatFilterOptionLabel(item?.text, filter.preserveOptionCase),
              };
            }
            return { value: item, label: formatFilterOptionLabel(item, filter.preserveOptionCase) };
          });

          return {
            key: filter.id,
            width: isCustomSelection ? customDynamicWidth : nonCustomFixedWidth,
            content: (
              <div ref={timeFilterAnchorRef}>
                <RadixSelectControl
                  value={filter.value ?? '__placeholder__'}
                  onValueChange={(val) => handleOnChange({ target: { name: filter?.name || filter.id, value: val === '__placeholder__' ? '' : val } } as any)}
                  onItemSelect={(selectedValue) => {
                    if (String(selectedValue) === "Custom") {
                      openCustomDatePopover();
                    }
                  }}
                  options={[{ value: '__placeholder__', label: capitalizeWords(filter.label) }, ...timeOptions]}
                  selectedLabelOverride={String(filter.value ?? "") === "Custom" ? customLabel : undefined}
                  className={filter.preserveOptionCase ? undefined : "capitalize-text"}
                  placeholder={capitalizeWords(filter.label)}
                  contentWidth="170px"
                />
              </div>
            ),
          };
        }

        if (filter.id === "regionFilter") {
          const regionOptions = (Array.isArray(filter.values) ? filter.values : []).map((v: any) =>
            typeof v === "string" ? { value: v, label: v } : { value: v.value, label: v.text ?? v.label ?? v.value }
          );
          const selectedRegionIds = Array.isArray(filter.value) ? filter.value : [];
          return {
            key: filter.id,
            width: parseWidth(filter.width),
            content: (
              <RadixFilterMultiSelect
                options={regionOptions}
                value={selectedRegionIds}
                onValueChange={(selectedIds) => {
                  filter.setValue && dispatch(filter.setValue(selectedIds));

                  const nextRegion = selectedIds.length === 1
                    ? geoRegions.find((region) => region.objectKey === selectedIds[0])
                    : undefined;

                  const statusFilter = filters.find((item) => item.id === "status");
                  const configFilter = filters.find((item) => item.id === "configstatus");
                  const kpiFilter = filters.find((item) => item.id === "kpistatus");

                  const isDisconnected = String(statusFilter?.value ?? "") === "19";
                  const isOffline = configFilter?.value === "Offline";
                  const isKpiError = kpiFilter?.value === "false";

                  const quickFilterKey =
                    isDisconnected ? "disconnected" :
                      isOffline ? "offline" :
                        isKpiError ? "pmCollected" :
                          "";

                  const nextNavItem = selectedIds.length === 0 ? quickFilterKey : "";

                  dispatch(setSelectedNavListItems(nextNavItem));
                  dispatch(setSelectedDeviceSiteGroup(""));
                  dispatch(setSelectedGeoRegion(nextRegion || initialGeoRegion));
                }}
                placeholder={capitalizeWords(filter.label)}
                width={filter?.width || "140px"}
              />
            ),
          };
        }

        // Default: single-select via RadixSelectControl
        return {
          key: filter.id,
          width: parseWidth(filter.width),
          content: (
            <RadixSelectControl
              value={filter.value ?? '__placeholder__'}
              onValueChange={(val) => handleOnChange({ target: { name: filter?.name || filter.id, value: val === '__placeholder__' ? '' : val } } as any)}
              options={
                typeof filter.values[0] === "object"
                  ? [{ value: '__placeholder__', label: filter.label.toUpperCase() === "KPI SYNC" ? "KPI Sync" : capitalizeWords(filter.label) }, ...filter.values.map((item: any) => ({ value: item?.value, label: formatFilterOptionLabel(item?.text, filter.preserveOptionCase) }))]
                  : ['__placeholder__', ...filter.values].map((item: any, idx: number) => typeof item === 'object' ? { value: item.value, label: formatFilterOptionLabel(item.text, filter.preserveOptionCase) } : { value: item, label: idx === 0 ? (filter.label.toUpperCase() === "KPI SYNC" ? "KPI Sync" : capitalizeWords(filter.label)) : formatFilterOptionLabel(item, filter.preserveOptionCase) })
              }
              className={filter.preserveOptionCase ? undefined : "capitalize-text"}
              placeholder={filter.label.toUpperCase() === "KPI SYNC" ? "KPI Sync" : capitalizeWords(filter.label)}
            />
          ),
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, geoRegions, dispatch, eventFromTimeFilter, eventToTimeFilter]);

  // ...existing code...
  return (
    <Stack direction="row" alignItems="center" spacing={1} className="min-w-0" sx={{ height: 32, minHeight: 32 }}>
      {showSearch !== false && (
        <div style={{ marginRight: 8 }} className="shrink-0">
          <SearchBox tooltip={searchTooltip} scope={searchScope} width={searchWidth} />
        </div>
      )}

      {overflowItems.length > 0 && (
        <OverflowFilterBar items={overflowItems} />
      )}
      <DateRangeCalendarPicker
        fromValue={eventFromTimeFilter}
        toValue={eventToTimeFilter}
        onFromChange={handleFromTimeOnChange}
        onToChange={handleToTimeOnChange}
        hideTrigger
        open={isCustomDatePopoverOpen}
        anchorEl={timeFilterAnchorRef.current}
        onClose={() => setIsCustomDatePopoverOpen(false)}
      />
      {isEnableApllFilter && (
        <MuiButton variant="outlined" startIcon={<FilterListIcon />} sx={{ ...selectdefaultSxprops, fontWeight: "bold", minWidth: "150px" }}>
          All Filters
        </MuiButton>
      )}
      <div style={{ marginLeft: "2px" }}>
        {children && children}
      </div>
      <div style={{ marginLeft: "7px" }} className="shrink-0 flex items-center self-center">
        <Tooltip title="Reset All">
          <span className="flex items-center">
            <Button
              variant="outline"
              className={`${resetButtonSizeClass} shrink-0 p-0 leading-none`}
              onClick={handleResetFilterOnClick}
              aria-label="Reset All"
              style={{ visibility: showReset ? 'visible' : 'hidden' }}
            >
              <FilterX className="h-4 w-4" />
            </Button>
          </span>
        </Tooltip>
      </div>
    </Stack>
  );
};

export default GeneralFilter;